<template>
  <NoticeToast :model="$store.state.toast" />

  <!-- Volle App-Shell (Sidebar, Topbar, Breadcrumbs) nur in der eigentlichen App.
       NICHT auf Onboarding-Wizard-Routen (auch wenn eingeloggt, z.B. incomplete-Tenant
       auf /onboarding/plan) -- der Wizard ist vollflaechig + bringt sein eigenes Layout. -->
  <template v-if="loggedIn && !isWizardRoute">
    <div class="app-shell">
      <AppSidebar v-if="!isMobile" />
      <div class="drawer-overlay" v-if="isMobile && drawerOpen" @click.self="drawerOpen = false">
        <AppSidebar class="drawer" @navigate="drawerOpen = false" />
      </div>
      <div class="app-content">
        <ImpersonationBanner v-if="$store.state.isImpersonation" />
        <!-- Zahlungs-Banner bei grace-Status -->
        <div class="grace-banner" v-if="tenantStatus === 'grace'">
          <span>Zahlung überfällig — Ihr Zugang wird bald gesperrt.</span>
          <button class="grace-banner-btn" :disabled="portalLoading" @click="openPortal">
            {{ portalLoading ? 'Wird geladen …' : 'Jetzt begleichen' }}
          </button>
        </div>
        <div class="topbar-row">
          <div class="page-head">
            <div class="wrapper-header-title-wrapper">
              <router-link v-if="this.breadCrumbs.length>0"
                           :to="this.breadCrumbs[this.breadCrumbs.length-1].link"
                           class="breadcrumb"
              >
                {{ this.breadCrumbs[this.breadCrumbs.length - 1].title }}
              </router-link>
              <div class="wrapper-header-title">
                <router-link v-if="this.breadCrumbs.length>0"
                             :to="this.breadCrumbs[this.breadCrumbs.length-1].link"
                             class="main-back"
                             v-html="getSVG('arrow_left')"
                />
                <h1>{{ pageTitle }}</h1>
              </div>
            </div>
          </div>
          <AppTopbar :isMobile="isMobile" @openDrawer="drawerOpen = true" />
        </div>
        <router-view name="tabBar" />
        <div class="scroll-wrapper">
          <div class="scroll-wrapper-inner">
            <OnboardingTour v-if="showTour" @finish="onTourFinish" />
            <router-view />
          </div>
        </div>
      </div>
    </div>
  </template>

  <!-- Nicht eingeloggt: nur der router-view (Auth-/Wizard-Seiten bringen ihr eigenes
       vollflaechiges, zentriertes Layout mit -- kein App-Chrome drumherum). -->
  <router-view v-else />

  <ConfirmationOverlay />
  <LibraryRightClickBox />
</template>

<script>
import NoticeToast from "@/components/elements/NoticeToast.vue";
import AppSidebar from "@/components/static/AppSidebar.vue";
import AppTopbar from "@/components/static/AppTopbar.vue";
import ConfirmationOverlay from "@/components/snippets/ConfirmationOverlay.vue";
import LibraryRightClickBox from "@/components/snippets/LibraryRightClickBox.vue";
import OnboardingTour from "@/components/elements/OnboardingTour.vue";
import ImpersonationBanner from "@/components/elements/ImpersonationBanner.vue";

export default {
  data() {
    return {
      portalLoading: false,
      drawerOpen: false,
      isMobile: window.matchMedia('(max-width: 900px)').matches,
    };
  },
  components: {
    AppSidebar,
    AppTopbar,
    NoticeToast,
    ConfirmationOverlay,
    LibraryRightClickBox,
    OnboardingTour,
    ImpersonationBanner,
  },
  computed: {
    pageTitle() {
      return this.$store.state.pageTitle
    },
    loggedIn() {
      return this.reloadUser();
    },
    // Onboarding-Wizard-Routen (/onboarding/*) sind vollflaechig -- KEIN App-Chrome,
    // auch wenn der Tenant schon eingeloggt (incomplete) ist.
    isWizardRoute() {
      const p = this.$route?.path || '';
      return p.startsWith('/onboarding') || p === '/verify-pending';
    },
    breadCrumbs() {
      return this.$store.state.breadCrumbs;
    },
    tenantStatus() {
      return this.$store.state.user?.tenantStatus ?? null;
    },
    showTour() {
      const user = this.$store.state.user;
      if (!user) return false;
      if (user.onboardingCompleted) return false;
      // Waehrend einer Support-Impersonation die Tour nicht zeigen -- sonst wuerde
      // "Ueberspringen" das Onboarding-Flag im Konto des Kunden setzen.
      if (user.isImpersonation) return false;
      const status = this.tenantStatus;
      return status === 'active' || status === 'grace';
    },
  },
  methods: {
    async onTourFinish() {
      try { await this.apiRequest('POST', '/account/completeOnboarding'); } catch(e) { /* nicht-blockierend */ }
      const user = this.$store.state.user;
      if(user) this.$store.commit('setUser', { ...user, onboardingCompleted: 1 });
    },
    async openPortal() {
      this.portalLoading = true;
      const res = await this.apiRequest('POST', '/billing/portal');
      this.portalLoading = false;
      if(res && res.success && res.url) {
        window.location.href = res.url;
      } else {
        this.showNotice({ type: 'error', content: 'Portal konnte nicht geöffnet werden.' });
      }
    },
    async loadTenantConfig() {
      const res = await this.apiRequest('GET', '/tenant', null, false);
      if(res && res.success) {
        this.$store.commit('setTenantConfig', res);
      }
    },
  },
  watch: {
    // Deckt alle Navigationsquellen ab (Nav-Links via @navigate UND
    // Profil-Popover/sonstige router.push-Aufrufe im Drawer) -- schliesst den
    // mobilen Drawer bei jedem Routenwechsel.
    '$route'() {
      this.drawerOpen = false;
    },
  },
  mounted() {
    document.addEventListener("contextmenu", this.openFileActionOverlay);
    this.loadTenantConfig();
    this._mq = window.matchMedia('(max-width: 900px)');
    this._mqHandler = e => {
      this.isMobile = e.matches;
      if (!e.matches) this.drawerOpen = false;
    };
    this._mq.addEventListener('change', this._mqHandler);
  },
  unmounted() {
    this._mq.removeEventListener('change', this._mqHandler);
  }
}
</script>

<style>
.grace-banner {
  background: var(--surface-soft);
  border-bottom: 1px solid var(--border-strong);
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: var(--ink);
}

.grace-banner-btn {
  background: var(--ink);
  color: #fff;
  border: none;
  border-radius: var(--radius-lg);
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.grace-banner-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.grace-banner-btn:hover:not(:disabled) {
  opacity: 0.85;
}
</style>
